/**
 * SettingsLayover — the shared scaffold every T12.2 settings surface renders
 * inside. Built to the prototype's per-surface layover pattern:
 *
 *   static compact header (one history-aware `‹ Back` + surface icon + title)
 *     → the 🧭 "how this steers your suggestions" band (household surfaces)
 *       or the 🛠️ neutral band (App settings)
 *     → the surface's content groups
 *
 * One mobile-first shell: on phone the surface is a full-screen routed view; on
 * desktop the same content sits beside a left settings sidebar (the drawer's
 * flat menu), driven by the `.desk-settings` rules in responsive.css. The
 * sidebar is rendered by <SettingsSidebar> and reflows out below the desktop
 * breakpoint so the phone surface is unchanged.
 *
 * T27.2.7.6:
 *   - MN3 — ONE back control. The in-content `‹ Back` is history-aware
 *     (`navigate(-1)`); the duplicate AppShell settings back → `/home` is gone.
 *     A `backTo` is honored as an override for sub-screens / deep links.
 *   - MN4 — the header is a normal block (no `position: sticky`), so scrolling
 *     content never bleeds into the band between the app chrome and the header.
 *   - MN5 — the surface tile is an `<Icon>`, not an emoji (pass `icon`); the
 *     `emoji` prop is kept for surfaces not yet migrated.
 *
 * The annotation band carries the prototype's verbatim copy (passed by each
 * surface). Band text meets AA contrast on its background: the 🧭 band uses the
 * accent tint, the 🛠️ band the muted neutral.
 *
 * All styles flow from tokens (CP5). No hex, no raw px literals.
 */

import type { CSSProperties, ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '../../components';
import { SETTINGS_MENU } from './settingsMenu';
import { usePublishLayoverTitle } from '../../stores/useLayoverTitle';

const layoutStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-6)',
};

const sidebarStyle: CSSProperties = {
  flex: '0 0 auto',
  width: '16rem',
  display: 'none', // shown at the desktop breakpoint via responsive.css.
  flexDirection: 'column',
  gap: 'var(--space-1)',
};

const contentStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-4)',
};

// MN4 — a normal (non-sticky) block. The AppShell renders the app chrome above
// every settings route; a SECOND pinned header was the redundancy that left
// scrolling content bleeding into the band between the two. Dropping
// `position: sticky` / `top` / `zIndex` makes the header scroll with its
// content, so nothing paints above its top edge.
const headerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  paddingTop: 'var(--space-2)',
  paddingBottom: 'var(--space-3)',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

const backlinkStyle: CSSProperties = {
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-semibold)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  background: 'none',
  border: 0,
  padding: 0,
  alignSelf: 'flex-start',
  minHeight: '2.75rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
};

const idRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
};

const tileStyle: CSSProperties = {
  width: '2.125rem',
  height: '2.125rem',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-lg)',
  flex: '0 0 auto',
};

const titleStyle: CSSProperties = {
  margin: 0,
  // T47 Phase 4 — settings section title → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
};

// 🧭 / 🛠️ annotation band. The 🧭 variant uses an accent-tinted background;
// the 🛠️ variant uses the neutral muted background. Both keep AA contrast on
// secondary text.
const bandBaseStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  alignItems: 'flex-start',
  borderRadius: 'var(--radius-lg)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  padding: 'var(--space-3)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 'var(--line-height-normal)',
};

// 🧭 steers band — accent text on the violet tint, brand-light border. The
// bold spans inside render in the accent (prototype `.steers b{color:brand}`)
// via the `.settings-steers-band` rule in responsive.css. T12.2 step 11b.
const steersBandStyle: CSSProperties = {
  ...bandBaseStyle,
  backgroundColor: 'var(--color-accent-subtle)',
  borderColor: 'var(--color-brand-purple-light)',
  color: 'var(--color-text-secondary)',
};

const neutralBandStyle: CSSProperties = {
  ...bandBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  borderColor: 'var(--color-border-subtle)',
  color: 'var(--color-text-secondary)',
};

const bandIconStyle: CSSProperties = {
  fontSize: 'var(--font-size-md)',
  flex: '0 0 auto',
  lineHeight: 'var(--line-height-tight)',
};

// ---------------------------------------------------------------------------
// Desktop settings sidebar — the drawer's flat menu as a left nav (MN1: one
// ordered list, no Household/App headings). Hidden on phone (the drawer is the
// phone nav); shown at the desktop breakpoint.
// ---------------------------------------------------------------------------

const sidebarLinkStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  textDecoration: 'none',
  color: 'var(--color-text-secondary)',
  minHeight: '2.75rem',
};

// Active sidebar link — accent text on the violet tint (prototype
// `.desk-nav.on`). T12.2 step 11b.
const sidebarLinkActiveStyle: CSSProperties = {
  ...sidebarLinkStyle,
  backgroundColor: 'var(--color-accent-subtle)',
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-semibold)',
};

function SettingsSidebar(): JSX.Element {
  return (
    <nav
      className="desk-settings-sidebar"
      style={sidebarStyle}
      aria-label="Settings"
      data-testid="settings-sidebar"
    >
      {SETTINGS_MENU.map((entry) => (
        <NavLink
          key={entry.to}
          to={entry.to}
          end
          style={({ isActive }) =>
            isActive ? sidebarLinkActiveStyle : sidebarLinkStyle
          }
          data-testid={`settings-sidebar-${entry.to.split('/').pop()}`}
        >
          <span aria-hidden="true">
            <Icon name={entry.icon} />
          </span>
          {entry.label}
        </NavLink>
      ))}
    </nav>
  );
}

export interface SettingsLayoverProps {
  /**
   * Surface tile. Prefer `icon` (the shared `<Icon>`, MN5); `emoji` is the
   * legacy fallback for surfaces not yet migrated off the emoji tile.
   */
  icon?: IconName;
  emoji?: string;
  title: string;
  /** Back label — `Back` from a surface, `Members` from a sub-screen. */
  backLabel: string;
  /**
   * Optional back override. By default the back is history-aware
   * (`navigate(-1)`) so it returns where the user came from (MN3); pass a
   * `backTo` only when a fixed target is wanted (e.g. a deep-linked sub-screen).
   */
  backTo?: string;
  /** The annotation band copy + variant. Omit to render no band. */
  band?: { variant: 'steers' | 'neutral'; children: ReactNode };
  /** data-testid for the surface root. */
  testId: string;
  children: ReactNode;
}

export function SettingsLayover({
  icon,
  emoji,
  title,
  backLabel,
  backTo,
  band,
  testId,
  children,
}: SettingsLayoverProps): JSX.Element {
  const navigate = useNavigate();
  // Publish this surface's title to the AppShell header center for as long as the
  // settings page is mounted (the same string shown in the in-content header
  // below), so the app header always reflects the current page's real name —
  // including dynamic sub-page titles like a member's name on Member detail.
  usePublishLayoverTitle(title);
  // MN3 — history-aware single back. `navigate(-1)` returns to wherever the
  // user came from (the menu, the deals list, a deep link's referrer); a
  // `backTo` override wins when a surface needs a fixed destination.
  const goBack = () => {
    if (backTo) navigate(backTo);
    else navigate(-1);
  };

  // The arrow is the `<Icon>` now (MN5); strip any legacy leading `‹ ` a caller
  // still passes in its label so the chevron isn't drawn twice.
  const backText = backLabel.replace(/^[‹<]\s*/, '');

  return (
    <div className="desk-settings-layout" style={layoutStyle} data-testid={testId}>
      <SettingsSidebar />
      <div style={contentStyle}>
        <div style={headerStyle}>
          <button
            type="button"
            style={backlinkStyle}
            onClick={goBack}
            data-testid={`${testId}-back`}
          >
            <Icon name="arrow-left" data-testid={`${testId}-back-icon`} />
            {backText}
          </button>
          <div style={idRowStyle}>
            <span style={tileStyle} aria-hidden="true">
              {icon ? (
                <Icon name={icon} data-testid={`${testId}-tile-icon`} />
              ) : (
                emoji
              )}
            </span>
            <h2 style={titleStyle}>{title}</h2>
          </div>
        </div>

        {band && (
          <div
            className={band.variant === 'steers' ? 'settings-steers-band' : undefined}
            style={band.variant === 'steers' ? steersBandStyle : neutralBandStyle}
            data-testid={`${testId}-band`}
          >
            <span style={bandIconStyle} aria-hidden="true">
              {band.variant === 'steers' ? '🧭' : '🛠️'}
            </span>
            <div>{band.children}</div>
          </div>
        )}

        {children}
      </div>
    </div>
  );
}
